import React, { useEffect, useRef, useState } from "react";
import { gsap } from "../../animations/gsap.config";
import { ScrollTrigger } from "../../animations/gsap.config";
import blogData from "../../data/blog.json";
import {
  Send,
  CheckCircle,
  Loader2,
  Mail,
  ArrowRight,
  Bell,
  Users,
  BookOpen,
  Zap,
} from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import api from "../../api/api";
import toast from "react-hot-toast";

/* ─────────────────────────────────────────────
 ANIMATED ORB
───────────────────────────────────────────── */
const FloatingOrb = ({ style, delay = 0 }) => {
  const ref = useRef(null);

  useEffect(() => {
    gsap.to(ref.current, {
      y: -20,
      x: 10,
      duration: 4 + delay,
      ease: "sine.inOut",
      repeat: -1,
      yoyo: true,
      delay,
    });
  }, [delay]);

  return (
    <div
      ref={ref}
      className="absolute rounded-full pointer-events-none"
      style={style}
    />
  );
};

/* ─────────────────────────────────────────────
 STATS ROW
───────────────────────────────────────────── */
const NewsStats = () => {
  const ref = useRef(null);
  const hasRun = useRef(false);
  const counts = useRef([]);

  const stats = [
    {
      icon: Users,
      value: 2000,
      suffix: "+",
      label: "Subscribers",
      color: "#3b82f6",
    },
    {
      icon: BookOpen,
      value: 50,
      suffix: "+",
      label: "Articles",
      color: "#f59e0b",
    },
    { icon: Bell, value: 1, suffix: "x/mo", label: "Digest", color: "#6366f1" },
    {
      icon: Zap,
      value: 100,
      suffix: "%",
      label: "Free Forever",
      color: "#10b981",
    },
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: ref.current,
        start: "top 85%",
        onEnter: () => {
          if (hasRun.current) return;
          hasRun.current = true;

          gsap.fromTo(
            ref.current?.children ?? [],
            { y: 20, opacity: 0, scale: 0.9 },
            {
              y: 0,
              opacity: 1,
              scale: 1,
              stagger: 0.1,
              duration: 0.5,
              ease: "back.out(1.4)",
            },
          );

          stats.forEach((s, i) => {
            const obj = { val: 0 };
            gsap.to(obj, {
              val: s.value,
              duration: 1.5 + i * 0.2,
              ease: "power2.out",
              delay: i * 0.1 + 0.3,
              onUpdate: () => {
                if (counts.current[i]) {
                  counts.current[i].textContent =
                    Math.round(obj.val) + s.suffix;
                }
              },
            });
          });
        },
      });
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={ref} className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
      {stats.map((s, i) => {
        const IconC = s.icon;
        return (
          <div
            key={i}
            className="rounded-2xl p-4 text-center"
            style={{
              background: `${s.color}08`,
              border: `1px solid ${s.color}20`,
              opacity: 0,
            }}
          >
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center
 mx-auto mb-2"
              style={{ background: `${s.color}20` }}
            >
              <IconC size={16} style={{ color: s.color }} />
            </div>
            <p
              ref={(el) => (counts.current[i] = el)}
              className="font-black font-mono text-xl"
              style={{ color: s.color }}
            >
              0{s.suffix}
            </p>
            <p className="text-[10px] text-gray-600 mt-0.5">{s.label}</p>
          </div>
        );
      })}
    </div>
  );
};

/* ─────────────────────────────────────────────
 EMAIL FORM
───────────────────────────────────────────── */
const EmailForm = ({ formData }) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const formRef = useRef(null);
  const inputRef = useRef(null);
  const lineRef = useRef(null);

  const handleFocus = () => {
    gsap.to(lineRef.current, {
      scaleX: 1,
      duration: 0.4,
      ease: "power3.out",
    });
    gsap.to(inputRef.current?.parentElement, {
      borderColor: "rgba(29,78,216,0.5)",
      duration: 0.3,
    });
  };

  const handleBlur = () => {
    if (!email) {
      gsap.to(lineRef.current, {
        scaleX: 0,
        duration: 0.3,
        ease: "power2.in",
      });
    }
    gsap.to(inputRef.current?.parentElement, {
      borderColor: "rgba(255,255,255,0.08)",
      duration: 0.3,
    });
  };
  const submitNewsLetterMutation = useMutation({
    mutationFn: (data) => {
      api.post("/api/newsletter/subscribe", { email: data });
    },
    onSuccess: () => {
      toast.success("Subscribed to NewsLetter!");
      setStatus("success");
    },
    onError: (err) => {
      toast.error(
        `Submission failed: ${err?.response?.data?.message || err.message}`,
      );
      setError(err?.response?.data?.message || err.message);
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      gsap.to(formRef.current, {
        x: [-6, 6, -4, 4, -2, 2, 0],
        duration: 0.35,
        ease: "none",
      });
      return;
    }
    submitNewsLetterMutation.mutate(email);
  };

  if (status === "success") {
    return (
      <div
        className="flex flex-col items-center justify-center gap-4
 py-8 text-center"
      >
        <div
          className="w-16 h-16 rounded-full bg-emerald-500/20
 border-2 border-emerald-500/40 flex items-center justify-center"
        >
          <CheckCircle size={28} className="text-emerald-600" />
        </div>
        <div>
          <p className="text-gray-900 font-bold text-lg">You're subscribed!</p>
          <p className="text-gray-600 text-sm mt-1">
            Check your inbox for a confirmation email.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-3">
      {/* Email input */}
      <div
        className="relative flex items-center rounded-xl overflow-hidden"
        style={{
          background: "rgba(255,255,255,0.8)",
          border: "1px solid rgba(0,0,0,0.08)",
        }}
      >
        <Mail
          size={16}
          className="absolute left-4 text-gray-500 pointer-events-none"
        />
        <input
          ref={inputRef}
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError("");
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={formData.placeholder}
          className="flex-1 bg-transparent pl-10 pr-4 py-4 text-sm
 text-gray-900 placeholder-gray-500 outline-none"
          required
        />
        {/* Focus line */}
        <div
          ref={lineRef}
          className="absolute bottom-0 left-0 right-0 h-[2px] origin-left"
          style={{
            background: "linear-gradient(90deg, #1d4ed8, #f59e0b)",
            transform: "scaleX(0)",
          }}
        />
      </div>

      {/* Error */}
      {error && (
        <p className="text-xs text-red-400 flex items-center gap-1.5">
          <span className="w-1 h-1 rounded-full bg-red-400" />
          {error}
        </p>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={submitNewsLetterMutation.isPending}
        className="group w-full relative inline-flex items-center justify-center
 gap-2.5 px-6 py-4 bg-secondary-500 hover:bg-secondary-400
 text-dark font-bold rounded-xl text-sm overflow-hidden
 transition-all duration-300 hover:-translate-y-0.5
 hover:shadow-glow-secondary disabled:opacity-60
 disabled:cursor-not-allowed"
      >
        <span
          className="absolute inset-0 translate-x-[-100%]
 group-hover:translate-x-[100%] transition-transform duration-700
 bg-gradient-to-r from-transparent via-white/20 to-transparent"
        />
        {status === submitNewsLetterMutation.isPending ? (
          <>
            <Loader2 size={15} className="animate-spin" />
            <span>Subscribing...</span>
          </>
        ) : (
          <>
            <Send size={15} />
            <span>{formData.buttonText}</span>
            <ArrowRight
              size={13}
              className="transition-transform duration-300
 group-hover:translate-x-1"
            />
          </>
        )}
      </button>

      {/* Privacy note */}
      <p className="text-center text-[11px] text-gray-500">
        {formData.privacy}
      </p>
    </form>
  );
};

/* ─────────────────────────────────────────────
 MAIN NEWSLETTER
───────────────────────────────────────────── */
const BlogNewsletter = () => {
  const { blog } = blogData;
  const { newsletter } = blog;

  const sectionRef = useRef(null);
  const cardRef = useRef(null);
  const headRef = useRef(null);
  const subRef = useRef(null);
  const formAreaRef = useRef(null);
  const shimmerRef = useRef(null);

  /* Reveal */
  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 80%",
        },
      });

      tl.fromTo(
        cardRef.current,
        { y: 50, opacity: 0, scale: 0.97 },
        { y: 0, opacity: 1, scale: 1, duration: 0.8, ease: "power3.out" },
      )
        .fromTo(
          headRef.current,
          { y: 25, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.5, ease: "power3.out" },
          "-=0.4",
        )
        .fromTo(
          subRef.current,
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.5, ease: "power3.out" },
          "-=0.3",
        )
        .fromTo(
          formAreaRef.current,
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.5, ease: "power3.out" },
          "-=0.2",
        );

      /* Shimmer loop */
      gsap.fromTo(
        shimmerRef.current,
        { x: "-100%" },
        {
          x: "200%",
          duration: 2.5,
          ease: "none",
          repeat: -1,
          delay: 1.5,
          repeatDelay: 4,
        },
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden section-py"
      style={{
        background: "linear-gradient(180deg, #f1f5f9 0%, #f8fafc 100%)",
      }}
    >
      {/* BG */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 dot-pattern opacity-[0.05]" />
        <div
          className="absolute top-0 left-0 right-0 h-24"
          style={{
            background: "linear-gradient(to bottom, #f1f5f9, transparent)",
          }}
        />
        <div
          className="absolute bottom-0 left-0 right-0 h-24"
          style={{
            background: "linear-gradient(to top, #f8fafc, transparent)",
          }}
        />
      </div>

      <div className="container-fluid relative z-10">
        <div
          ref={cardRef}
          className="relative max-w-2xl mx-auto rounded-3xl overflow-hidden p-8 sm:p-12"
          style={{
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.8) 100%)",
            border: "1px solid rgba(29,78,216,0.3)",
            backdropFilter: "blur(20px)",
            opacity: 0,
          }}
        >
          {/* Shimmer sweep */}
          <div
            ref={shimmerRef}
            className="absolute inset-y-0 w-32 pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)",
            }}
          />

          {/* Floating orbs */}
          <FloatingOrb
            style={{
              top: "-30px",
              right: "10%",
              width: "120px",
              height: "120px",
              background:
                "radial-gradient(circle, rgba(245,158,11,0.12) 0%, transparent 70%)",
              filter: "blur(20px)",
            }}
            delay={0}
          />
          <FloatingOrb
            style={{
              bottom: "-20px",
              left: "5%",
              width: "100px",
              height: "100px",
              background:
                "radial-gradient(circle, rgba(29,78,216,0.15) 0%, transparent 70%)",
              filter: "blur(20px)",
            }}
            delay={1.5}
          />

          {/* Grid pattern */}
          <div className="absolute inset-0 grid-pattern opacity-10 pointer-events-none" />

          {/* Content */}
          <div className="relative z-10 text-center space-y-6">
            {/* Headline */}
            <h2
              ref={headRef}
              className="font-display font-black text-gray-900 leading-tight"
              style={{
                fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
                opacity: 0,
              }}
            >
              {newsletter.headline}
            </h2>

            {/* Subheadline */}
            <p
              ref={subRef}
              className="text-gray-700 leading-relaxed text-[15px] max-w-md mx-auto"
              style={{ opacity: 0 }}
            >
              {newsletter.subheadline}
            </p>

            {/* Stats */}
            <NewsStats />

            {/* Form */}
            <div ref={formAreaRef} style={{ opacity: 0 }}>
              <EmailForm formData={newsletter} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogNewsletter;
